import { supabase } from "@/integrations/supabase/client";

export interface BulkResumeHeaderInfo {
  fullName: string;
  phoneNumber: string;
  email: string;
  cityST: string;
  linkedin: string;
}

export interface BulkResumeExperience {
  jobTitle: string;
  company: string;
  location: string;
  startDate: string;
  endDate: string;
  bullets: string[];
}

export interface BulkResumeEducation {
  degree: string;
  school: string;
  location: string;
  graduationDate: string;
}

export interface BulkResumeData {
  headerInfo: BulkResumeHeaderInfo;
  summary: string;
  experience: BulkResumeExperience[];
  education: BulkResumeEducation[];
  skills: string[];
  resumeIdentifier?: string;
}

export interface BulkResumeRequest {
  resumes: BulkResumeData[];
}

interface BulkResumeResponse {
  success: boolean;
  files: {
    filename: string;
    data: string; // base64 encoded — the POLISHED bytes when a template map exists
    size: number;
    /** docx-polish report for this exact render: { ok, modified, fixes, blockers,
     *  sourceSha256, polishedSha256, renderId } — or { unavailable, reason } when
     *  polish did not run. Attached server-side in generate-bulk-resumes. */
    polish?: Record<string, unknown> | null;
  }[];
  totalFiles: number;
}

export class BulkResumeService {
  /** The client's own Word file for the "Original Format" design (set by the results page when known). */
  static originalDocxUrl: string | null = null;

  static async generateBulkResumes(data: any, templateId: string = 'template1'): Promise<BulkResumeResponse> {
    try {
      // Handle both direct resume object and wrapped in resumes array
      let resumeCount = 1;
      if (data.resumes && Array.isArray(data.resumes)) {
        resumeCount = data.resumes.length;
      } else if (data.Name) {
        resumeCount = 1;
      }

      console.log('Generating bulk resumes:', {
        resumeCount: resumeCount,
        templateId: templateId
      });

      // Add templateId to the request body
      const requestBody: Record<string, unknown> = {
        ...data,
        templateId,
        ...(templateId === 'original-format' && BulkResumeService.originalDocxUrl ? { originalDocxUrl: BulkResumeService.originalDocxUrl } : {}),
      };
      if (templateId === 'original-format' && !BulkResumeService.originalDocxUrl) {
        return { success: false, files: [], error: 'Original Format needs the client\'s own Word file. This run has no .docx on file.' } as any;
      }

      const { data: response, error } = await supabase.functions.invoke('generate-bulk-resumes', {
        body: requestBody
      });

      if (error) {
        console.error('Error calling generate-bulk-resumes function:', error);
        // supabase-js hides the function's response body behind "non-2xx
        // status code"; the body carries the real reason (e.g. Docmosis
        // "Document Generation Disabled due to Unpaid Account"). Read it.
        let detail = error.message;
        try {
          const ctx = (error as any)?.context;
          if (ctx && typeof ctx.text === 'function') {
            const bodyText = await ctx.text();
            try { detail = JSON.parse(bodyText)?.error || bodyText; } catch { detail = bodyText || detail; }
          }
        } catch { /* keep the generic message */ }
        throw new Error(`Bulk resume generation failed: ${detail}`);
      }

      if (!response || !response.success) {
        throw new Error(`Bulk resume generation failed: ${(response as any)?.error || 'no documents returned'}`);
      }

      console.log('Bulk generation successful:', response);
      return response as BulkResumeResponse;

    } catch (error) {
      console.error('Error in BulkResumeService.generateBulkResumes:', error);
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to generate bulk resumes'
      );
    }
  }

  static downloadFileFromBase64(filename: string, base64Data: string): void {
    try {
      // Decode base64 to bytes
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      
      // Create blob and download
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      
      this.downloadBlob(blob, filename);
    } catch (error) {
      console.error('Error downloading file:', filename, error);
      throw new Error(`Failed to download ${filename}`);
    }
  }

  static downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}